import { Injectable, inject } from '@angular/core';
import { firstValueFrom, map as rxMap, tap } from 'rxjs';
import { AuthenticationService } from '../../authentication/authentication.service';
import { FirebaseEventService } from '../../authentication/firebase.event.service';
import { CableCreateDto, CableCreateRequestDto, CableSummaryDto, CableUpdateRequestDto, MceApiService } from '../../core/mceapi.client.generated';
import { appOpt } from '../../core/mceapi.client.helpers';

function requireCableUid(uid: string | undefined): string {
  if (!uid) throw new Error('Cable DTO is missing its UID.');
  return uid;
}

@Injectable({
  providedIn: 'root',
})
export class CableEditService {

  private authenticationService = inject(AuthenticationService);
  private readonly mceApiService = inject(MceApiService);
  private firebaseEventService = inject(FirebaseEventService);

  /// Creates a new cable and returns the created cable.
  /// Not saved in the database until the cable is saved.
  async createCable(projectUid: string): Promise<CableCreateDto> {
    let request: CableCreateRequestDto = { projectUid: projectUid };
    return firstValueFrom(this.mceApiService.v1CablesCreateCable(request));
  }

  async getCable(cableUid: string) {
    return firstValueFrom(this.mceApiService.v1CablesGet(cableUid));
  }

  async getCableComments(cableUid: string) {
    return firstValueFrom(this.mceApiService.v1CablesComments(cableUid).pipe(
      rxMap(x => appOpt(x))
    ));
  }

  async copyCable(cable: CableSummaryDto): Promise<CableSummaryDto> {
    const cableUid = requireCableUid(cable.uid);

    return firstValueFrom(
      this.mceApiService.v1CablesCopy(cableUid).pipe(
        tap(copiedCable => {
          if (cable.projectUid && copiedCable.uid) {
            this.firebaseEventService.logCopyCableEvent(
              this.authenticationService.currentUser(), cable.projectUid, cable.projectUid, copiedCable.uid);
          }
        }))
    );
  }

  async copyCables(cables: CableSummaryDto[]): Promise<CableSummaryDto[]> {
    const uids = cables.map(c => requireCableUid(c.uid));
    return firstValueFrom(
      this.mceApiService.v1CablesUidsCopy(uids).pipe(
        tap(copiedCables => {
          copiedCables.forEach(copiedCable => {
            const originalCable = cables.find(c => c.uid === copiedCable.uid);
            if (originalCable?.projectUid && originalCable.uid && copiedCable.uid) {
              this.firebaseEventService.logCopyCableEvent(
                this.authenticationService.currentUser(),
                originalCable.projectUid, originalCable.uid, copiedCable.uid);
            } });
        }))
    );
  }

  async moveCable(cable: CableSummaryDto, toProjectUid: string): Promise<CableSummaryDto> {
    const cableUid = requireCableUid(cable.uid);

    return firstValueFrom(
      this.mceApiService.v1CablesMoveProjectUid(cableUid, toProjectUid).pipe(
        tap(movedCable => {
          if (movedCable.projectUid) {
            this.firebaseEventService.logMoveCableEvent(
              this.authenticationService.currentUser(), cableUid, movedCable.projectUid);
          }
        }))
    );
  }

  async moveCables(cables: CableSummaryDto[], toProjectUid: string): Promise<CableSummaryDto[]> {
    const uids = cables.map(c => requireCableUid(c.uid));
    return firstValueFrom(
      this.mceApiService.v1CablesUidsMoveProjectUid(toProjectUid,uids).pipe(
        tap(movedCables => {
          movedCables.forEach(movedCable => {
            const originalCable = cables.find(c => c.uid === movedCable.uid);
            if (originalCable?.uid && movedCable.projectUid) {
              this.firebaseEventService.logMoveCableEvent(
                this.authenticationService.currentUser(), originalCable.uid, movedCable.projectUid);
            }
          });
        }))
    );
  }

  async deleteCable(cable: CableSummaryDto): Promise<void> {
    const cableUid = requireCableUid(cable.uid);

    return firstValueFrom(
      this.mceApiService.v1CablesDelete(cableUid).pipe(
        tap(() => {
          if (cable.projectUid) {
            this.firebaseEventService.logDeleteCableEvent(
              this.authenticationService.currentUser(), cable.projectUid, cableUid);
          }
        }))
    );
  }

  async deleteCables(cables: CableSummaryDto[]): Promise<void> {
    const uids = cables.map(c => requireCableUid(c.uid));
    return firstValueFrom(
      this.mceApiService.v1CablesUidsDelete(uids).pipe(
        tap(() => {
          cables.forEach((cable, index) => {
            if (!cable.projectUid) return;

            this.firebaseEventService.logDeleteCableEvent(
              this.authenticationService.currentUser(), cable.projectUid, uids[index]);
          });
        }))
    );
  }

  async saveNewCable(request: CableUpdateRequestDto): Promise<CableSummaryDto> {
    return firstValueFrom(
      this.mceApiService.v1CablesUpdateCable(request).pipe(
        tap(savedCable => {
          if (savedCable.projectUid && savedCable.uid) {
            this.firebaseEventService.logAddCableEvent(
              this.authenticationService.currentUser(), savedCable.projectUid, savedCable.uid);
          }
        }))
    );
  }


  async updateCable(request: CableUpdateRequestDto): Promise<CableSummaryDto> {
    return firstValueFrom(
      this.mceApiService.v1CablesUpdateCable(request).pipe(
        tap(updatedCable => {
          if (updatedCable.projectUid && updatedCable.uid) {
            this.firebaseEventService.logSaveCableEvent(
              this.authenticationService.currentUser(), updatedCable.projectUid, updatedCable.uid);
          }
        }))
    );
  }

}
